import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const About = () => {
  const { language } = useLanguage();
  const bannerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Floating CTA buttons (same as other pages)
  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  // Block content - alternating image pattern
  // Block 1: image RIGHT, Block 2: image LEFT, Block 3: image RIGHT, Block 4: image LEFT
  const blocks = [
    {
      imagePosition: 'right',
      text: language === 'fr'
        ? "Ocean Peak Group est né de la conviction que l'industrie européenne mérite des partenaires capables d'opérer là où la pression est élevée, avec maîtrise, humilité et engagement de long terme. Nous agissons dans la durée et au cœur des réalités opérationnelles, aux côtés des entreprises industrielles françaises et européennes, pour renforcer leurs fondamentaux, sécuriser leurs trajectoires et faire émerger des champions industriels à l'échelle mondiale."
        : "Ocean Peak Group was founded on the conviction that European industry deserves partners willing to operate where the pressure is real, with rigour, humility, and unwavering long-term commitment. We work over the long run and at the heart of operational realities, alongside European industrial businesses, to strengthen their foundations, secure their trajectories, and build world-class industrial champions.",
      image: 'https://customer-assets.emergentagent.com/job_capital-connect-15/artifacts/xklw0y9w_PHOTO-2025-12-11-16-05-46%204.jpg'
    },
    {
      imagePosition: 'left',
      text: language === 'fr'
        ? "Ocean Peak Group opère à travers deux entités complémentaires, chacune conçue pour maximiser la création de valeur. Ensemble, elles forment une plateforme intégrée qui combine expertise en investissement et accompagnement opérationnel."
        : "Ocean Peak Group operates through two complementary entities, each designed to maximise value creation. Together, they form an integrated platform combining investment expertise with hands-on operational support.",
      image: 'https://customer-assets.emergentagent.com/job_capital-connect-15/artifacts/42a8n3al_PHOTO-2025-12-11-16-05-47%204.jpg'
    },
    {
      imagePosition: 'right',
      text: language === 'fr'
        ? "Lancée en 2023, Ocean Peak Capital est notre entité de conseil en investissement financier (CIF). Capital conseille des investisseurs professionnels et institutionnels sur les options pour investir à nos côtés, en direct ou au travers de nos fonds et véhicules dédiés. Ocean Peak Capital agit en tant que CIF auprès de Tygrow SAS pour la gestion d'Ocean Peak Capital Partners."
        : "Ocean Peak Capital is our regulated investment advisory entity (CIF, supervised by the AMF), providing investment advice to professional investors and advising Tygrow SAS as manager of Ocean Peak Capital Partners. Capital also chairs the consortium vehicle which controls Moret Industries Group.",
      image: 'https://customer-assets.emergentagent.com/job_capital-connect-15/artifacts/3fsr5pjz_PHOTO-2025-12-11-16-05-47%202.jpg'
    },
    {
      imagePosition: 'left',
      text: language === 'fr'
        ? "Créée en septembre 2025, Ocean Peak Management est notre plateforme d'accompagnement des entreprises en phase de transformation. Nous intervenons dans tous les domaines d'une organisation pour fournir un soutien opérationnel."
        : "Established in September 2025, Ocean Peak Management is our platform for supporting businesses through periods of transformation. We intervene across every function of an organisation to deliver operational support, both within and beyond our portfolio.",
      image: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/u8jqbtnv_VetterTec%20Workshop_Overview%20with%20screw%20conveyor%20in%20front.JPG'
    }
  ];

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Floating CTA Buttons on the side */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner - Same height as other pages (550px), mountain image */}
      <section 
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div 
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: 'url(/DIV.jpeg)',
            backgroundPosition: 'center 30%'
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10, 31, 53, 0.5)' }} />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1
            className="text-5xl lg:text-6xl text-white text-center mb-6"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'QUI SOMMES-NOUS' : 'ABOUT US'}
          </h1>
          <p
            className="text-xl lg:text-2xl text-white text-center max-w-4xl mx-auto font-bold"
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              lineHeight: 1.6
            }}
          >
            {language === 'fr'
              ? "Votre partenaire vers l'excellence industrielle et l'ambition à long terme."
              : "Your partner for industrial excellence and long-term ambition."}
          </p>
        </div>
      </section>

      {/* Content Blocks - Alternating Images */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 space-y-20 lg:space-y-32">
          {blocks.map((block, idx) => (
            <motion.div
              key={idx}
              className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
            >
              {/* For imagePosition 'right': Text first (left), Image second (right) */}
              {/* For imagePosition 'left': Image first (left), Text second (right) */}
              
              {block.imagePosition === 'right' ? (
                <>
                  {/* Text on Left */}
                  <div>
                    <p
                      className="text-lg text-steel-grey leading-relaxed"
                      style={{
                        fontFamily: "'Eurostile', 'Arial', sans-serif",
                        lineHeight: 1.9
                      }}
                    >
                      {block.text}
                    </p>
                  </div>
                  {/* Image on Right */}
                  <div className="overflow-hidden group">
                    <img
                      src={block.image}
                      alt="Ocean Peak"
                      className="w-full h-[350px] lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Image on Left */}
                  <div className="overflow-hidden group">
                    <img
                      src={block.image}
                      alt="Ocean Peak"
                      className="w-full h-[350px] lg:h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  {/* Text on Right */}
                  <div>
                    <p
                      className="text-lg text-steel-grey leading-relaxed"
                      style={{
                        fontFamily: "'Eurostile', 'Arial', sans-serif",
                        lineHeight: 1.9
                      }}
                    >
                      {block.text}
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};
